import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema } from "@/lib/seo";

const UPDATED = "2026-09-29";
const ANSWER =
  "Wenn Besucher kommen, aber niemand kauft, liegt es meist an einem von drei Gründen: Die falschen Besucher kommen, der Shop schafft kein Vertrauen, oder der Weg zur Kasse hat Reibung. In den meisten Shops finden sich die Ursachen in wenigen Stunden, oft ohne teuren Relaunch.";

const SITE = "https://shopify-freelancer.net";
const PATH = "/fragen/warum-kauft-niemand-in-meinem-shop";
const TITLE = "Warum kauft niemand in meinem Shopify-Shop?";
const DESCRIPTION =
  "Besucher da, aber keine Käufe? Die drei häufigsten Ursachen und wie Sie herausfinden, welche bei Ihrem Shop zutrifft.";

const faqs = [
  {
    q: "Woran erkenne ich, dass die falschen Besucher kommen?",
    a: "Wenn viele Besucher nach wenigen Sekunden gehen oder aus Ländern kommen, in die Sie nicht liefern, stimmt die Quelle nicht. Das sehen Sie in den Besucherstatistiken.",
  },
  {
    q: "Was schafft Vertrauen im Shop?",
    a: "Echte Bewertungen, klare Versand- und Rückgabeinfos, erreichbare Kontaktdaten, saubere Produktdarstellung und ein Impressum, das man findet.",
  },
  {
    q: "Wo springen Käufer am häufigsten ab?",
    a: "Am Warenkorb und an der Kasse: überraschende Versandkosten, fehlende Zahlarten oder ein langer Bestellweg sind die klassischen Gründe.",
  },
  {
    q: "Brauche ich dafür einen kompletten Relaunch?",
    a: "Meist nicht. Die häufigsten Bremsen lassen sich gezielt beheben, ohne den Shop neu zu bauen.",
  },
];

export const Route = createFileRoute("/fragen/warum-kauft-niemand-in-meinem-shop")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Conversion",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({
        question: "Warum kauft niemand in meinem Shopify-Shop?",
        answer: ANSWER,
        path: PATH,
        updated: UPDATED,
      }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Verkaufen"
      title="Warum kauft niemand in meinem Shopify-Shop?"
      intro="Kurz gesagt: fast immer einer von drei Gründen — falsche Besucher, fehlendes Vertrauen oder Reibung auf dem Weg zur Kasse. Alle drei lassen sich finden und beheben."
      source="frage-niemand-kauft"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Grund 1: Die falschen Besucher",
          p: [
            "WerbeAnzeigen oder Suchbegriffe, die Neugierige statt Käufer bringen, sind die häufigste Ursache. Ein Blick in die Besucherquellen zeigt das schnell.",
          ],
        },
        {
          h: "Grund 2: Fehlendes Vertrauen",
          p: [],
          list: [
            "Keine oder unglaubwürdige Bewertungen",
            "Versandkosten und Lieferzeit unklar",
            "Kein erkennbarer Ansprechpartner",
            "Produktfotos oder Texte wirken lieblos",
          ],
        },
        {
          h: "Grund 3: Reibung bis zur Kasse",
          p: [],
          list: [
            "Überraschende Kosten erst im Warenkorb",
            "Gängige Zahlarten fehlen",
            "Zu viele Schritte oder Pflichtfelder",
            "Am Handy schwer bedienbar",
          ],
        },
        {
          h: "Der schnellste Weg zur Antwort",
          p: [
            "Eine gezielte Prüfung Ihres Shops zeigt in wenigen Stunden, welcher der drei Gründe bei Ihnen zutrifft und was sich zuerst lohnt. Das ist günstiger als ein Relaunch auf Verdacht.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-conversion-optimierung", label: "Conversion-Optimierung" },
        { to: "/shopify-seo", label: "Shopify SEO" },
        { to: "/roi-rechner", label: "ROI-Rechner" },
      ]}
    />
  );
}
